import React, { useCallback, useEffect, useState } from 'react'
import { SafeAreaView } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useFocusEffect } from '@react-navigation/native'
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs'
import { GetUnitConfig, TestEmail, SaveUnitConfig, GetUnitConfigParams, GetUnits } from '@/domain/usecases'
import { Box, Button, currentUnitState, HStack, Loading, Text, UnitsSelector, unitsState } from '@/presentation/components'
import { useSettings, useThemeClient, useMediaQueries, useErrorHandler } from '@/presentation/hooks'
import {
  InvoicesSettings,
  GeneralSettings,
  FinancialSettings,
  ServiceOrderSettings,
  MenuAccessPermissions,
  ModalOnSaveOrDiscard,
  ModalOnDiscardChanges,
  ModalOnSaveChanges,
  isLoadingState,
  ModalSettingsDetails,
  isLoadingSaveConfigState,
  isLoadingSaveOrDiscardConfigState
}
  from '@/presentation/pages/Settings/components'
import { styles } from './styles'

type SettingsProps = {
  getUnitConfigs: GetUnitConfig
  saveUnitConfig: SaveUnitConfig
  getUnits: GetUnits
  testEmail: TestEmail
}

const Tab = createMaterialTopTabNavigator()

export const Settings: React.FC<SettingsProps> = (props) => {
  const handleError = useErrorHandler()
  const [currentUnit, setCurrentUnit] = useRecoilState(currentUnitState)
  const { getThemeClient } = useThemeClient()
  const { isMobile } = useMediaQueries()
  const { startConfig } = useSettings()
  const [isLoading, setLoading] = useRecoilState(isLoadingState)
  const setUnits = useSetRecoilState(unitsState)
  const [loadedUnitCode, setLoadedUnitCode] = useState<number | null>(null)
  const [retry, setRetry] = useState(0)
  const [unitsRetry, setUnitsRetry] = useState(0)
  const isSavingConfig = useRecoilValue(isLoadingSaveConfigState)
  const isSavingOrDiscarding = useRecoilValue(isLoadingSaveOrDiscardConfigState)

  const themeClient = getThemeClient()

  useEffect(() => {
    let active = true
    setLoading(true)
    props.getUnits
      .getAll()
      .then(units => {
        if (!active) return
        if (units[0]) {
          setUnits(units)
          setCurrentUnit(units[0])
        } else {
          setLoading(false)
        }
      })
      .catch(error => {
        if (active) {
          handleError(error)
          setLoading(false)
        }
      })
    return () => { active = false }
  }, [unitsRetry])

  useFocusEffect(
    useCallback(() => {
      let active = true
      setLoadedUnitCode(null)
      if (currentUnit) {
        const getConfigParams: GetUnitConfigParams = {
          codUnidade: currentUnit.codigo
        }

        setLoading(true)
        props.getUnitConfigs
          .getAll(getConfigParams)
          .then(config => {
            if (active) {
              startConfig(config)
              setLoadedUnitCode(currentUnit.codigo)
            }
          })
          .catch(error => { if (active) handleError(error) })
          .finally(() => { if (active) setLoading(false) })
      }
      return () => { active = false }
    }, [currentUnit, retry])
  )

  const financialSettings = (): JSX.Element => <FinancialSettings />
  const generalSettings = (): JSX.Element => <GeneralSettings testEmail={props.testEmail} />
  const invoicesSettings = (): JSX.Element => <InvoicesSettings />
  const serviceOrderSettings = (): JSX.Element => <ServiceOrderSettings />
  const menuAccessPermissions = (): JSX.Element => <MenuAccessPermissions />

  return (
    <SafeAreaView style={styles.container}>
      <Box style={styles.content} >
        <HStack justify='center' items='center' style={{
          backgroundColor: `${themeClient.mainColor}CC`
        }}>
          <Text variant="subtitle1" style={{ ...styles.headerText, color: themeClient.menuColor }} >
            Configurações da Unidade
          </Text>
          <UnitsSelector disabled={isLoading || isSavingConfig || isSavingOrDiscarding} />
        </HStack>
        <Box style={styles.content}>
          {isLoading
            ? <Loading text='Buscando configurações' />
            : loadedUnitCode === currentUnit?.codigo
              ? <Tab.Navigator
              screenOptions={{
                tabBarItemStyle: { paddingHorizontal: 40 },
                tabBarLabelStyle: { color: themeClient.menuColor },
                tabBarIndicatorStyle: { backgroundColor: themeClient.menuColor },
                tabBarStyle: { backgroundColor: themeClient.mainColor }
              }}
            >
              <Tab.Screen
                options={{
                  tabBarLabel: 'Financeiro',
                  title: 'Financeiro - Portal do Cliente'
                }}
                name='financeiro'
                component={financialSettings}
              />
              <Tab.Screen
                options={{
                  tabBarLabel: 'Gerais',
                  title: 'Configurações Gerais - Portal do Cliente'
                }}
                name='gerais'
                component={generalSettings}
              />
              <Tab.Screen
                options={{
                  tabBarLabel: isMobile ? 'Notas' : 'Notas Fiscais',
                  title: 'Notas Fiscais - Portal do Cliente'
                }}
                name='notas-fiscais'
                component={invoicesSettings}
              />
              <Tab.Screen
                options={{
                  tabBarLabel: isMobile ? 'OSs' : 'Ordens de Serviço',
                  title: 'Ordens de Serviço - Portal do Cliente'
                }}
                name='ordens-servico'
                component={serviceOrderSettings}
              />
              <Tab.Screen
                options={{
                  tabBarLabel: isMobile ? 'Menus' : 'Permissão de Menus',
                  title: 'Permissão de Menu - Portal do Cliente'
                }}
                name='permissao-menus'
                component={menuAccessPermissions}
              />
            </Tab.Navigator>
              : <Box style={{ padding: 16 }}>
                <Text>Não foi possível carregar as configurações da unidade.</Text>
                <Button title='Tentar novamente' onPress={() => {
                  if (currentUnit) setRetry(value => value + 1)
                  else setUnitsRetry(value => value + 1)
                }} />
              </Box>
          }
        </Box>

        {!isLoading && loadedUnitCode === currentUnit?.codigo && <>
          <ModalSettingsDetails />
          <ModalOnSaveOrDiscard saveUnitConfig={props.saveUnitConfig} />
          <ModalOnSaveChanges saveUnitConfig={props.saveUnitConfig} />
          <ModalOnDiscardChanges />
        </>}
      </Box>
    </SafeAreaView>
  )
}
